import type {
  ApiAiSettingsDto,
  ApiAiState,
  ApiAiTestResult,
  ApiAuditEvent,
  ApiContact,
  ApiMediaUpload,
  ApiSettings,
  ApiContactDetail,
  ApiConversation,
  ApiMessage,
  ApiMessageStatus,
  ApiMessageType,
  ApiPreview,
  ApiDoc,
  ApiFaq,
  ApiTestModeState,
  ApiTicket,
  ApiUser,
  ApiWaStatus
} from './apiTypes';
import type {
  AiSettings,
  AiState,
  AiTestResult,
  AuditEvent,
  Contact,
  MediaUpload,
  PanelConfig,
  Conversation,
  Doc,
  Faq,
  Message,
  MessageKind,
  MessageStatus,
  TestModeState,
  TestPreviewResult,
  Ticket,
  User,
  WaStatus
} from './types';

/** Backend DTO → panel tipi dönüşümleri. Tüm alan eşlemeleri burada; ekranlar ham DTO görmez. */

export function toUser(user: ApiUser): User {
  return {
    id: user.id,
    name: user.name || user.email,
    email: user.email,
    // admin, sahip yetkileriyle çalışır (permissions dizisi asıl kaynaktır).
    role: user.role === 'agent' ? 'agent' : 'owner'
  };
}

/**
 * "Son denemeler" yalnız gönderim denemelerini gösterir (guard_block, send, limit...).
 * Bağlantı (reconnect), mod değişimi (mode_change), eşleştirme ve oturum olayları listelenmez.
 */
const OUTBOUND_ATTEMPT_PATTERN = /guard|send|outbound|attempt|limit|quiet|content|media/i;

function mapAiState(value: ApiAiState): AiState {
  if (value === 'human_takeover') return 'human';
  if (value === 'paused') return 'paused';
  return 'ai';
}

function mapKind(type: ApiMessageType): MessageKind {
  switch (type) {
    case 'image':
    case 'video':
      return 'image';
    case 'document':
      return 'document';
    case 'audio':
      return 'audio';
    case 'system':
      return 'system';
    default:
      return 'text';
  }
}

function mapStatus(status: ApiMessageStatus, direction: 'inbound' | 'outbound'): MessageStatus | undefined {
  if (direction === 'inbound') return undefined;
  switch (status) {
    case 'pending':
      return 'queued';
    case 'sent':
      return 'sent';
    case 'delivered':
      return 'delivered';
    case 'read':
      return 'read';
    case 'failed':
      return 'failed';
    case 'blocked':
      return 'blocked';
    default:
      return undefined;
  }
}

function previewBody(preview: ApiConversation['lastMessage']): string {
  if (!preview) return '—';
  if (preview.body) return preview.body;
  if (preview.type === 'image' || preview.type === 'video') return 'Görsel';
  if (preview.type === 'document') return 'Belge';
  if (preview.type === 'audio') return 'Ses kaydı';
  return '—';
}

export function toConversation(conversation: ApiConversation): Conversation {
  const contact = conversation.contact;
  return {
    id: conversation.id,
    contactId: contact.id,
    phone: contact.phoneE164,
    name: contact.displayName || contact.pushName || null,
    lastMessage: previewBody(conversation.lastMessage),
    lastAt: conversation.lastMessageAt,
    unread: conversation.unreadCount,
    status: conversation.status,
    assignedTo: conversation.assigneeId,
    aiState: mapAiState(conversation.aiState),
    labels: conversation.labels.map((label) => label.name),
    // Gerçek API'de test işareti ayrı alan değil; etiketle taşınırsa buradan okunur.
    isTest: conversation.labels.some((label) => label.name === 'test'),
    waitingSince: conversation.status === 'closed' ? null : conversation.lastInboundAt
  };
}

export function toMessage(message: ApiMessage): Message {
  const author: Message['author'] = message.isAi
    ? 'ai'
    : message.direction === 'inbound'
      ? 'customer'
      : 'agent';
  const kind = mapKind(message.type);
  const mediaName = message.mediaMime
    ? message.mediaMime.startsWith('image/')
      ? 'Görsel'
      : message.mediaMime.includes('pdf')
        ? 'Belge (PDF)'
        : 'Medya'
    : undefined;

  return {
    id: message.id,
    conversationId: message.conversationId,
    author: kind === 'system' ? 'system' : author,
    kind,
    body: message.body || (kind === 'system' ? '' : mediaName || ''),
    at: message.createdAt,
    status: mapStatus(message.status, message.direction),
    isNote: message.isInternalNote,
    hasMedia: message.hasMedia,
    mediaName: message.hasMedia ? mediaName : undefined,
    mediaMime: message.mediaMime ?? undefined,
    mediaSize: message.mediaSize ?? undefined,
    blockedReason: message.errorCode ?? undefined
  };
}

/** API mesajları yeniden eskiye döner; panel yukarıdan aşağıya gösterir. */
export function sortMessages(messages: ApiMessage[]): Message[] {
  return messages
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .map(toMessage);
}

/** GET /api/contacts satırı: contact + summary (konuşma sayıları). */
export function toContactListItem(raw: ApiContact & { summary?: { conversationCount?: number; openConversationCount?: number } }): Contact {
  const base = toContact(raw);
  return { ...base, ticketCount: raw.summary?.conversationCount ?? 0 };
}

export function toContact(contact: ApiContact): Contact {
  return {
    id: contact.id,
    phone: contact.phoneE164,
    name: contact.displayName || contact.pushName || null,
    optIn: contact.optIn === 'opted_in',
    optInAt: contact.optInAt,
    createdAt: contact.firstSeenAt,
    labels: [],
    notes: contact.notes || '',
    // Sipariş verisi fizipazar/AnkaraQR adaptörüyle (F8) gelecek; şimdilik boş.
    orders: [],
    ticketCount: 0,
    lastSeenAt: contact.lastSeenAt,
    blocked: contact.isBlocked
  };
}

export function toContactDetail(detail: ApiContactDetail): Contact {
  const base = toContact(detail.contact);
  return { ...base, ticketCount: detail.recentConversations.length };
}

export function toTestModeState(state: ApiTestModeState): TestModeState {
  const lastAttempt = state.recentAttempts[0];
  return {
    enabled: state.mode !== 'LIVE',
    runtimeMode: state.mode,
    allowlist: state.allowlist.map((entry) => ({
      phone: entry.phoneE164,
      active: entry.active,
      addedAt: entry.createdAt
    })),
    counters: {
      blocked24h: state.counters.blockedOutbox24h + state.counters.policyBlocks24h,
      allowed24h: state.counters.sent24h,
      outboundToday: state.counters.sent24h,
      dailyCap: state.limits.daily,
      lastError: lastAttempt?.reason ?? null
    },
    // Son denemeler: yalnız gönderim denemeleri. Bağlantı/eşleştirme/oturum olayları gösterilmez.
    attempts: state.recentAttempts
      .filter((attempt) => OUTBOUND_ATTEMPT_PATTERN.test(attempt.kind))
      .map((attempt) => ({
        id: attempt.id,
        at: attempt.createdAt,
        to: attempt.phoneMasked || '—',
        decision: attempt.kind.includes('block') || (attempt.reason || '').includes('BLOCK') ? 'blocked' : 'allowed',
        reason: attempt.reason || attempt.kind,
        kind: 'text'
      }))
  };
}

export function toPreview(preview: ApiPreview, fallbackTarget: string): TestPreviewResult {
  const reason = preview.reason || preview.note || (preview.allowed ? 'İzinli hedef' : 'Guard engelledi');
  return {
    allowed: Boolean(preview.allowed),
    reason,
    guard: preview.allowed ? 'ALLOW' : 'BLOCK',
    target: preview.target?.phoneE164 || preview.target?.maskedPhone || fallbackTarget
  };
}

export function toWaStatus(raw: ApiWaStatus | null, mode: TestModeState['runtimeMode'] = 'TEST'): WaStatus {
  const status = raw?.status || 'disconnected';
  return {
    // Gösterge yalnız gerçekten bağlıyken yeşil olmalı; 'connecting' sırasında connected=true gelse bile bağlı sayılmaz.
    connected: status === 'connected',
    status,
    qr: raw?.qr ?? null,
    pairingCode: raw?.pairingCode ?? null,
    phone: raw?.phoneMasked ?? null,
    device: raw?.device || '—',
    connectedAt: raw?.connectedAt ?? null,
    browserIdentity: raw?.browserIdentity || 'ubuntu',
    mode: raw?.mode ?? mode
  };
}

export function toTicket(ticket: ApiTicket): Ticket {
  const status: Ticket['status'] =
    ticket.status === 'closed' ? 'resolved' : ticket.status === 'open' ? 'open' : 'pending';
  return {
    id: ticket.id,
    no: ticket.no,
    subject: ticket.subject,
    email: ticket.email,
    phone: ticket.phone || '',
    status,
    priority: ticket.priority,
    channel: ticket.channel,
    slaDueAt: ticket.slaDueAt,
    createdAt: ticket.createdAt,
    assignee: ticket.assignee || ticket.assigneeId,
    summary: ticket.summary || '',
    aiSources: ticket.aiSources ?? [],
    messages: (ticket.messages ?? []).map((message) => ({
      id: message.id,
      channel: message.channel,
      author: message.author,
      body: message.body,
      at: message.at,
      subject: message.subject ?? undefined
    })),
    conversationId: ticket.conversationId,
    isTest: ticket.isTest
  };
}

export function toFaq(faq: ApiFaq): Faq {
  return {
    id: faq.id,
    question: faq.question,
    answer: faq.answer,
    category: faq.category || 'Genel',
    tags: faq.tags ?? [],
    active: faq.active,
    updatedAt: faq.updatedAt,
    // API kullanım sayacı tutmuyor; panelde 0 gösterilir.
    hits: 0
  };
}

export function toDoc(doc: ApiDoc): Doc {
  const status: Doc['status'] =
    doc.status === 'ready' ? 'ready' : doc.status === 'failed' ? 'failed' : doc.status === 'uploaded' ? 'queued' : 'processing';
  return {
    id: doc.id,
    name: doc.title,
    sizeBytes: 0,
    pages: doc.pages ?? 0,
    chunks: doc.chunkCount ?? 0,
    status,
    uploadedAt: doc.createdAt,
    indexedAt: doc.status === 'ready' ? doc.createdAt : null,
    proposedFaqs: doc.faqCandidateCount ?? 0,
    error: doc.error ?? undefined,
    approved: doc.approved
  };
}

export function toAuditEvent(event: ApiAuditEvent): AuditEvent {
  const actor = event.actor;
  const actorLabel =
    typeof actor === 'string'
      ? actor
      : actor
        ? actor.name || actor.email || actor.id
        : 'Sistem';
  const target = event.target || [event.entity, event.entityId].filter(Boolean).join(':') || '—';
  const meta = event.meta ?? event.detail;
  const metaText = meta === undefined || meta === null ? null : typeof meta === 'string' ? meta : JSON.stringify(meta);
  // Audit satırında ayrı 'result' alanı yok; sonuç eylem adı ve detaydan türetilir.
  const result: AuditEvent['result'] = /fail|denied|block|reject|invalid/i.test(event.action)
    ? 'denied'
    : metaText && /"error"\s*:/.test(metaText)
      ? 'error'
      : 'ok';
  return {
    id: String(event.id),
    at: event.at || event.createdAt || new Date().toISOString(),
    actor: actorLabel,
    action: event.action,
    target,
    ip: event.ip || event.actorIp || '—',
    result,
    meta: metaText
  };
}

export function toSettings(dto: ApiSettings): PanelConfig {
  return {
    panelName: dto.panelName ?? 'Destek Merkezi',
    supportEmail: dto.supportEmail ?? '',
    timezone: dto.timezone ?? 'Europe/Istanbul',
    businessHoursStart: dto.businessHoursStart ?? '09:00',
    businessHoursEnd: dto.businessHoursEnd ?? '18:00',
    testRecipients: dto.testRecipients ?? []
  };
}

export function toAiTestResult(raw: ApiAiTestResult): AiTestResult {
  const decision = raw.decision || (raw.wouldEscalate ? 'handoff' : 'answer');
  const fallback =
    decision === 'abstain'
      ? 'AI bu soruda yanıt vermedi (abstain); e-posta isteyip destek talebi açtı.'
      : decision === 'ticket'
        ? 'AI destek talebi açtı; müşteriye bekleme süresi bildirildi.'
        : decision === 'handoff'
          ? 'Konuşma bir ajana devredildi.'
          : 'Yanıt üretilemedi.';
  return {
    answer: raw.answer || raw.error || fallback,
    confidence: typeof raw.confidence === 'number' ? raw.confidence : 0,
    sources: Array.isArray(raw.sources) ? raw.sources : [],
    wouldEscalate: raw.wouldEscalate ?? decision !== 'answer',
    latencyMs: raw.latencyMs ?? raw.latency_ms ?? 0,
    decision
  };
}

export function toMediaUpload(raw: ApiMediaUpload): MediaUpload {
  const media = raw.media ?? {};
  return {
    id: raw.id ?? raw.mediaId ?? media.id ?? '',
    url: raw.url ?? raw.mediaUrl ?? media.url ?? '',
    mime: raw.mime ?? media.mime ?? 'application/octet-stream',
    size: raw.size ?? media.size ?? 0,
    name: raw.name ?? raw.filename ?? media.name ?? 'medya'
  };
}

export function toAiSettings(dto: ApiAiSettingsDto): AiSettings {
  const effort = dto.settings.effort;
  const allowed: AiSettings['effort'][] = ['off', 'low', 'medium', 'high', 'max'];
  return {
    model: dto.settings.model,
    effort: (allowed as string[]).includes(effort) ? (effort as AiSettings['effort']) : 'low',
    confidenceThreshold: dto.settings.confidenceThreshold,
    dailyBudgetUsd: dto.settings.dailyBudgetUsd,
    promptExtra: dto.settings.promptExtra,
    autoReply: dto.settings.autoReply,
    maxRepliesPerConversation: dto.settings.maxRepliesPerConversation,
    apiKeyConfigured: dto.apiKey?.configured,
    apiKeySource: dto.apiKey?.source,
    updatedAt: dto.updatedAt,
    updatedBy: dto.updatedBy
  };
}

/** Backend uçları henüz yokken (404/501) gösterilecek durum. */
export function unavailableWaStatus(mode: TestModeState['runtimeMode'] = 'TEST'): WaStatus {
  return {
    connected: false,
    status: 'unavailable',
    qr: null,
    pairingCode: null,
    phone: null,
    device: '—',
    connectedAt: null,
    browserIdentity: 'ubuntu',
    mode,
    unavailable: true
  };
}
